/** Small pieces shared by the dairy screens. */
import type { ReactNode } from "react";
import { Link } from "react-router";
import { localDate, type Me, type Quantity } from "../../../contract";
import { formatDate, formatNumber, formatQuantity } from "../../../web/lib/format";
import { plural, t } from "../../../web/lib/i18n";
import { queryClient, useApi } from "../../../web/lib/query";
import { useMe } from "../../../web/lib/session";
import { Chip, TagLabel, cx } from "../../../web/ui/kit";
import { FRESH_BADGE_DAYS, type FreshFlag, type MilkUnit, type ReviewFlag } from "../shared/lactation";
import type { CowRow, SummaryDTO } from "../shared/types";
import { APPETITE, KETONES, QUARTER_LABELS } from "../contract";

/** Is this a dairy? From the cached session, for code that can't use hooks (animal actions). */
export function dairyPresetCached(): boolean {
  const me = queryClient.getQueryData<Me | null>(["/me"]);
  return !!me?.ranch?.presets?.includes("dairy");
}

/** The ranch's local date, from the cached session when there is one. */
export function ranchToday(): string {
  const tz = queryClient.getQueryData<Me | null>(["/me"])?.ranch?.timezone;
  try {
    return tz ? localDate(new Date(), tz) : new Date().toISOString().slice(0, 10);
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

/** Dairy screens and settings show for a dairy preset or once there are dairy records. */
export function useIsDairy(): { dairy: boolean; loading: boolean } {
  const { data: me } = useMe();
  const preset = !!me?.ranch?.presets?.includes("dairy");
  const summary = useApi<SummaryDTO>(preset ? null : "/m/dairy/summary", { staleTime: 60_000 });
  return { dairy: preset || !!summary.data?.has_data, loading: !preset && summary.isLoading };
}

export function useUnits(): { system: "imperial" | "metric"; milk: MilkUnit } {
  const { data } = useMe();
  const system = data?.ranch?.units === "metric" ? "metric" : "imperial";
  return { system, milk: system === "metric" ? "kg" : "lb" };
}

export const milkText = (q: Quantity | null | undefined, system: "imperial" | "metric") => (q ? formatQuantity(q, system) : "");
export const amountText = (value: number | null | undefined, unit: MilkUnit) => (value === null || value === undefined ? "–" : `${formatNumber(value)} ${t(unit)}`);

export function dimText(dim: number | null | undefined): string {
  if (dim === null || dim === undefined) return t("Fresh date unknown");
  return plural(dim, "{{count}} day in milk", "{{count}} days in milk");
}

/** "Lactation 3 · 45 days in milk" or "Dry 20 days". */
export function cowState(c: Pick<CowRow, "status" | "lactation" | "dim" | "dry_on">, today: string): string {
  const parts: string[] = [];
  if (c.lactation) parts.push(t("Lactation {{number}}", { number: c.lactation }));
  if (c.status === "milking" || c.status === "calved") parts.push(dimText(c.dim));
  if (c.status === "dry" && c.dry_on) {
    const days = Math.round((Date.parse(`${today}T12:00:00Z`) - Date.parse(`${c.dry_on}T12:00:00Z`)) / 86_400_000);
    parts.push(plural(days, "Dry {{count}} day", "Dry {{count}} days"));
  }
  if (c.status === "calved") parts.push(t("Calved, no dairy record yet"));
  return parts.join(" · ");
}

export function CowTitle({ cow, size = "md" }: { cow: Pick<CowRow, "id" | "tag" | "label" | "barn_name">; size?: "sm" | "md" | "lg" }) {
  return (
    <Link to={`/cattle/${cow.id}`} className="dairy-cow">
      {cow.tag ? <TagLabel tag={cow.tag} size={size} /> : <span className="dairy-cow-label">{cow.label}</span>}
      {cow.barn_name && <span className="dairy-barn">{cow.barn_name}</span>}
    </Link>
  );
}

export function SccChip({ scc, limit, previous }: { scc: number | null | undefined; limit: number; previous?: number | null }) {
  if (scc === null || scc === undefined) return null;
  const high = scc >= limit;
  const fresh = high && (previous === null || previous === undefined || previous < limit);
  return (
    <Chip tone={high ? "bad" : "neutral"} icon={high ? "warn" : undefined} title={t("Somatic cell count, thousand cells per mL")}>
      {t("SCC {{value}}k", { value: scc })}{high ? ` · ${fresh ? t("new") : t("again")}` : ""}
    </Chip>
  );
}

/** Why a lactation needs a look. The strings are listed in the contract's messages for the catalog. */
export const REVIEW_TEXT: Record<ReviewFlag, string> = {
  no_dry_off: "No dry-off was recorded before the next lactation.",
  extra_dry_off: "More than one dry-off is recorded in this lactation.",
  record_after_dry_off: "Milk was recorded after this lactation's dry-off.",
  start_unknown: "The fresh date isn't known.",
  start_estimated: "Fresh date estimated from the lab's days in milk.",
};

const FLAG_LABELS: Record<FreshFlag, string> ={ fever: "Fever", ketosis: "Ketones", off_feed: "Off feed", discharge: "Discharge", da: "DA ping", down: "Down" };

export function FlagChips({ flags }: { flags: FreshFlag[] }) {
  if (!flags.length) return <Chip tone="good" icon="done">{t("All clear")}</Chip>;
  return <span className="badges">{flags.map((f) => <Chip key={f} tone="bad" icon="warn">{t(FLAG_LABELS[f])}</Chip>)}</span>;
}

export const quarterText = (qs: string[]) => qs.map((q) => t(QUARTER_LABELS[q as keyof typeof QUARTER_LABELS] ?? q)).join(", ");
export const ketonesText = (v: string | undefined) => (v ? t(KETONES[v as keyof typeof KETONES] ?? v) : "");
export const appetiteText = (v: string | undefined) => (v ? t(APPETITE[v as keyof typeof APPETITE] ?? v) : "");

export const isFresh = (dim: number | null | undefined) => dim !== null && dim !== undefined && dim <= FRESH_BADGE_DAYS;

export function untilText(until: string | undefined | null): string {
  return until ? t("through {{date}}", { date: formatDate(until) }) : t("until put back");
}

/** A tappable cow row for workspace lists: big tag, plain state, and the one action that matters. */
export function CowListRow({ cow, meta, chips, action, className }: { cow: Pick<CowRow, "id" | "tag" | "label" | "barn_name">; meta?: ReactNode; chips?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <li className={cx("dairy-row", className)}>
      <div className="dairy-row-main">
        <CowTitle cow={cow} size="lg" />
        {meta && <span className="dairy-row-meta">{meta}</span>}
        {chips && <span className="badges">{chips}</span>}
      </div>
      {action && <div className="dairy-row-action">{action}</div>}
    </li>
  );
}
